import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertCircle, ChevronRight, Printer, Download, FileText, AlertTriangle, CheckCircle, HelpCircle } from "lucide-react";
import { api, API } from "@/lib/api";
import { ConfidenceBadge, RevisionMeta, CiteThis, PrimaryDocuments } from "@/components/shared";
import { Reveal } from "@/components/collection";

/* ============================================================================
   BB-067A: Elvis Aloha 1973 — Facts & Corrections Pages
   Scholarly myth-vs-documentation presentation
   ============================================================================ */

const ClaimTypeIcon = ({ level }) => {
  switch (level) {
    case 'High':
      return <CheckCircle className="w-5 h-5 text-[hsl(145,50%,45%)]" />;
    case 'Medium':
      return <HelpCircle className="w-5 h-5 text-[hsl(43,74%,52%)]" />;
    case 'Low':
    default:
      return <AlertTriangle className="w-5 h-5 text-[hsl(355,60%,50%)]" />;
  }
};

export const FactsIndexPage = () => {
  const [facts, setFacts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    api.get("/facts").then(res => {
      setFacts(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }} data-testid="facts-index-page">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(222,47%,11%)] to-[hsl(0,0%,4%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,hsl(355,76%,33%,0.06)_0%,transparent_50%)]" />
        
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-ui text-xs uppercase tracking-[0.25em] text-[hsl(355,76%,33%)] mb-4">
              Correcting the Record
            </p>
            <h1 className="font-display text-4xl md:text-5xl text-white mb-6">Fact Correction Index</h1>
            <p className="font-editorial text-lg text-white/60 leading-relaxed max-w-2xl">
              Systematic documentation and correction of repeatedly cited inaccuracies about the Aloha from Hawaii concert.
              Each entry traces the claim's origin, spread mechanism, and evidence status.
            </p>
          </Reveal>
        </div>
      </section>
      
      {/* Facts List */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/20 border-t-[hsl(355,76%,33%)]"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {facts.map((fact, idx) => (
                <Reveal key={fact.id} delay={Math.min(idx * 50, 200)}>
                  <Link 
                    to={`/facts/${fact.slug}`}
                    className="group block p-6 md:p-8 bg-[hsl(222,47%,11%,0.5)] border border-white/10 hover:border-[hsl(355,76%,33%,0.4)] transition-colors"
                    data-testid={`fact-correction-${fact.slug}`}
                  >
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-4">
                          <AlertCircle className="w-5 h-5 text-[hsl(355,60%,50%)]" />
                          <span className="font-ui text-xs uppercase tracking-[0.15em] text-[hsl(355,60%,60%)]">
                            Common Claim Under Review
                          </span>
                        </div>
                        <h3 className="font-display text-xl md:text-2xl text-white group-hover:text-[hsl(355,60%,60%)] transition-colors mb-4">
                          &ldquo;{fact.claim_summary}&rdquo;
                        </h3>
                        {fact.evidence_review && (
                          <p className="font-editorial text-white/60 leading-relaxed line-clamp-2">{fact.evidence_review}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-3 flex-shrink-0">
                        <ConfidenceBadge level={fact.confidence_level} />
                        <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-[hsl(355,60%,60%)]" />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
              
              {facts.length === 0 && !loading && (
                <div className="text-center py-16 text-white/50 font-editorial">
                  <p className="mb-2">No published fact corrections are currently available.</p>
                  <p className="text-sm text-white/40">
                    Corrections will appear here only after source verification and editorial review of commonly repeated claims.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Archive Pack Download */}
          <Reveal delay={300}>
            <div className="mt-12 p-8 bg-[hsl(222,47%,11%,0.5)] border border-white/10">
              <h3 className="font-display text-xl text-white mb-3 flex items-center gap-3">
                <Download className="w-5 h-5 text-[hsl(43,74%,52%)]" />
                Archive Pack Download
              </h3>
              <p className="font-editorial text-white/60 mb-6 leading-relaxed">
                Download the complete Fact Corrections Index Pack as a single PDF document. Organized alphabetically 
                with full evidence reviews and cross-references for scholarly citation.
              </p>
              <a
                href={`${API}/pdf/archive/facts`}
                download
                className="inline-flex items-center gap-2 px-6 py-3 bg-[hsl(43,74%,52%)] text-black font-ui text-sm font-semibold uppercase tracking-wider hover:bg-white transition-colors"
                data-testid="download-facts-archive-btn"
              >
                <FileText className="w-4 h-4" />
                Download Fact Corrections Index Pack (PDF)
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export const FactDetailPage = () => {
  const { slug } = useParams();
  const [fact, setFact] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    api.get(`/facts/${slug}`).then(res => {
      setFact(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);
  
  if (loading) {
    return (
      <div className="flex justify-center py-20" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/20 border-t-[hsl(355,76%,33%)]"></div>
      </div>
    );
  }
  
  if (!fact) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center" style={{ backgroundColor: '#0a0a0a' }}>
        <p className="font-editorial text-white/50 mb-4">Fact correction not found</p>
        <Link to="/facts" className="font-ui text-sm text-[hsl(355,76%,33%)] hover:underline">
          ← Back to Fact Corrections
        </Link>
      </div>
    );
  }
  
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  return (
    <div className="min-h-screen reading-surface" data-testid="fact-detail-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <Link to="/facts" className="font-ui text-sm text-[hsl(355,76%,33%)] hover:underline inline-flex items-center gap-1">
            ← Back to Fact Corrections
          </Link>
          <div className="flex items-center gap-3">
            <a 
              href={`${API}/pdf/facts/${slug}`}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[hsl(0,0%,80%)] text-[hsl(0,0%,40%)] font-ui text-sm hover:border-[hsl(355,76%,33%)] hover:text-[hsl(355,76%,33%)] transition-colors"
              data-testid="export-pdf-btn"
              download
            >
              <Download className="w-4 h-4" />
              Export PDF
            </a>
            <Link 
              to={`/print/facts/${slug}`}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[hsl(0,0%,80%)] text-[hsl(0,0%,40%)] font-ui text-sm hover:border-[hsl(355,76%,33%)] hover:text-[hsl(355,76%,33%)] transition-colors"
              data-testid="print-fact-btn"
            >
              <Printer className="w-4 h-4" />
              Print
            </Link>
          </div>
        </div>
        
        {/* Article */}
        <article className="bg-white border-l-4 border-[hsl(355,76%,33%)] p-8 md:p-10">
          <header className="mb-10 pb-8 border-b border-[hsl(0,0%,90%)]">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <AlertCircle className="w-6 h-6 text-[hsl(355,60%,50%)]" />
              <span className="font-ui text-xs uppercase tracking-[0.15em] text-[hsl(355,60%,45%)]">Claim Under Review</span>
              <ConfidenceBadge level={fact.confidence_level} />
            </div>
            <h1 className="font-display text-2xl md:text-3xl text-[hsl(0,0%,10%)] leading-tight">
              &ldquo;{fact.claim_summary}&rdquo;
            </h1>
          </header>
          
          <div className="space-y-8">
            <section>
              <h2 className="font-ui text-xs uppercase tracking-[0.2em] text-[hsl(355,76%,33%)] mb-3">
                Origin of Claim
              </h2>
              <p className="font-editorial text-[hsl(0,0%,30%)] leading-relaxed">
                {fact.origin || "Origin documentation pending"}
              </p>
            </section>
            
            <section>
              <h2 className="font-ui text-xs uppercase tracking-[0.2em] text-[hsl(355,76%,33%)] mb-3">
                Mechanism of Spread
              </h2>
              <p className="font-editorial text-[hsl(0,0%,30%)] leading-relaxed">
                {fact.spread_mechanism || "Spread analysis pending"}
              </p>
            </section>
            
            <section>
              <h2 className="font-ui text-xs uppercase tracking-[0.2em] text-[hsl(355,76%,33%)] mb-3">
                Evidence Review
              </h2>
              <p className="font-editorial text-[hsl(0,0%,20%)] leading-relaxed text-lg">
                {fact.evidence_review || "Evidence review pending"}
              </p>
            </section>
          </div>
          
          <div className="mt-10 pt-8 border-t border-[hsl(0,0%,90%)]">
            <RevisionMeta updatedAt={fact.updated_at} />
          </div>
          
          {/* Primary Documents */}
          <PrimaryDocuments 
            entityType="facts" 
            entityId={fact.id}
            title="Supporting Documents"
          />
          
          <CiteThis 
            title={`Fact Correction: ${fact.claim_summary}`}
            date={fact.updated_at}
            url={pageUrl}
          />
        </article>
      </div>
    </div>
  );
};

export default FactsIndexPage;
